import { faCheck } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import CredibilityProofScreen from "./CredibilityProofScreen"
import ChevronBackButton from "../../components/buttons/ChevronBackButton"
import Primary3DButton from "../../components/buttons/Primary3DButton"
import LinearProgress from "../../components/linearProgress/LinearProgress"
import OnboardingPageContentWrapper from "../../components/onboarding/onboardingPageContentWrapper/OnboardingPageContentWrapper"
import OnboardingPageWrapper from "../../components/onboarding/onboardingPageWrapper/OnboardingPageWrapper"
import { BACKGROUND_COLORS, prepaywallQuestions } from "../../constants"
import useDocumentTitle from "../../hooks/useDocumentTitle"
import useMobile from "../../hooks/useMobile"
import { useUserQuery } from "../../networking/queries"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { requireAuth } from "../../util/auth"
import { useRouter } from "../../util/router"

const PrePaywallQuestionnaire = () => {
  useDocumentTitle("Joon - Start Trial")
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [isImageTransitioning, setIsImageTransitioning] = useState(false)
  const [showCredibilityProof, setShowCredibilityProof] = useState(false)
  const { user } = useUserQuery()
  const router = useRouter()
  const isMobile = useMobile()
  const { t } = useTranslation()

  useEffect(() => {
    trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_PREPAYWALL_STATEMENTS)
  }, [])

  const onBack = () => {
    if (currentQuestionIndex === 0) return
    setIsImageTransitioning(true)
    setTimeout(() => {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
      setTimeout(() => setIsImageTransitioning(false), 100)
    }, 150)
  }
  const onYes = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.PRE_PAYWALL_STATEMENT_RESONATES, {
      statement: prepaywallQuestions[currentQuestionIndex].imageText,
    })
    setIsImageTransitioning(true)
    setTimeout(() => {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      setTimeout(() => setIsImageTransitioning(false), 100)
    }, 150)
  }
  const onNo = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.PRE_PAYWALL_STATEMENT_NO_RESONATES, {
      statement: prepaywallQuestions[currentQuestionIndex].imageText,
    })
    setIsImageTransitioning(true)
    setTimeout(() => {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
      setTimeout(() => setIsImageTransitioning(false), 100)
    }, 150)
  }
  const goToPaywall = () => {
    router.push("/paywall/trial")
  }

  const onContinue = () => {
    // Show the credibility-proof screen before the paywall.
    setShowCredibilityProof(true)
  }

  if (showCredibilityProof) {
    return <CredibilityProofScreen onContinue={goToPaywall} />
  }

  if (
    !user ||
    currentQuestionIndex >= prepaywallQuestions.length ||
    currentQuestionIndex < 0
  )
    return <></>

  // Keep the constant for the image asset (and analytics still reads the
  // English imageText off it); translate the displayed copy by index here.
  const { image } = prepaywallQuestions[currentQuestionIndex]
  const isFirstQuestion = currentQuestionIndex === 0
  const isLastQuestion = currentQuestionIndex === prepaywallQuestions.length - 1

  const statementKeys = [
    "PAYWALL_PREPAYWALL_STATEMENT_1",
    "PAYWALL_PREPAYWALL_STATEMENT_2",
    "PAYWALL_PREPAYWALL_STATEMENT_3",
  ] as const
  const question = isLastQuestion
    ? t("PAYWALL_PREPAYWALL_FINAL_Q_FREE")
    : t("PAYWALL_PREPAYWALL_RELATE_Q")
  const imageText = isLastQuestion ? "" : t(statementKeys[currentQuestionIndex])

  return (
    <OnboardingPageWrapper
      style={{
        backgroundImage:
          isMobile && isLastQuestion
            ? "url(/images/background/cloud-bg.png)"
            : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <OnboardingPageContentWrapper
        paddingTop={SPACING.space6}
        backgroundOnDesktop={BACKGROUND_COLORS.xlightBlueBg}
        backgroundImageOnDesktop={
          isLastQuestion ? "url(/images/background/cloud-bg.png)" : "none"
        }
      >
        <div className="flex flex-row items-center w-full justify-center gap-2">
          <ChevronBackButton
            onClick={onBack}
            style={{
              position: "relative",
              top: "",
              left: "",
              opacity: isFirstQuestion ? 0 : 1,
              pointerEvents: isFirstQuestion ? "none" : "auto",
              transition: "opacity 0.3s ease-in-out",
            }}
          />
          <LinearProgress
            value={
              (currentQuestionIndex / (prepaywallQuestions.length - 1)) * 100
            }
            withIcon
          />
        </div>
        <div className="flex flex-col items-center gap-2">
          <Typography variant="h2Serif" textAlign="center" className="px-6">
            {question}
          </Typography>
        </div>
        <div
          className="flex flex-col items-center rounded-lg"
          style={{
            width: "min(100%, 350px)",
            background: isLastQuestion
              ? "transparent"
              : JoonColorExpanded.orange100,
            opacity: isImageTransitioning ? 0 : 1,
            transition: "opacity 0.2s ease-in-out",
          }}
        >
          {!isLastQuestion && (
            <Typography
              variant="h4"
              textAlign="center"
              className="p-4"
              color={JoonColorExpanded.orange700}
            >
              {imageText}
            </Typography>
          )}
          <img
            src={image}
            alt={image}
            className="object-contain w-full pointer-events-none rounded-b-lg"
            style={{ userSelect: "none" }}
          />
        </div>
        {isLastQuestion ? (
          <div className="flex flex-col items-center gap-2 w-full sticky bottom-2">
            <div className="flex flex-row items-center gap-1">
              <Typography variant="bodyBold" color={JoonUIColor.text.primary}>
                {t("PAYWALL_NO_PAYMENT_REQUIRED")}
              </Typography>
              <FontAwesomeIcon
                icon={faCheck}
                color={JoonUIColor.text.primary}
              />
            </div>
            <Primary3DButton
              text={t("PAYWALL_PREPAYWALL_TRY_NOW")}
              onClick={onContinue}
            />
          </div>
        ) : (
          <div className="flex flex-row items-center gap-2 w-full sticky bottom-2">
            <AnswerButton onClick={onNo} answer={t("INVITE_NO")} />
            <AnswerButton onClick={onYes} answer={t("INVITE_YES")} />
          </div>
        )}
      </OnboardingPageContentWrapper>
    </OnboardingPageWrapper>
  )
}

function AnswerButton({
  onClick,
  answer,
}: {
  onClick: () => void
  answer: string
}) {
  const [isHovered, setIsHovered] = useState(false)
  const [isSelected, setIsSelected] = useState(false)

  const onSelectAnswer = () => {
    if (isSelected) return

    setIsSelected(true)
    onClick()
    setTimeout(() => {
      setIsSelected(false)
    }, 250)
  }

  return (
    <button
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background:
          !isHovered || isSelected
            ? JoonUIColor.background.primaryNeutral
            : JoonUIColor.background.xlightGray,
        border: isSelected
          ? `1px solid ${JoonUIColor.border.accent}`
          : `1px solid ${JoonUIColor.border.default}`,
        color: isSelected
          ? JoonUIColor.text.primaryAccent
          : JoonUIColor.text.primary,
        borderRadius: SPACING.space2,
        padding: SPACING.space4,
        width: "100%",
        minHeight: "56px",
        transition: "border, background 0.3s ease-in-out",
        userSelect: "none",
        pointerEvents: isSelected ? "none" : "auto",
        cursor: isSelected ? "not-allowed" : "pointer",
      }}
      onClick={onSelectAnswer}
    >
      <Typography
        variant="body"
        textAlign="center"
        style={{
          width: "100%",
          height: "100%",
          color: isSelected
            ? JoonUIColor.text.primaryAccent
            : JoonUIColor.text.primary,
        }}
      >
        {answer}
      </Typography>
    </button>
  )
}

const PrePaywallQuestionnaireWithAuth = requireAuth(PrePaywallQuestionnaire)
export default PrePaywallQuestionnaireWithAuth
